import { FC } from 'react';
import styled from '@emotion/styled';
import Dialog from '@comp/Dialog/Dialog';
import Close from '@icon/Close';
import { Fonts, Spacings, breakpoints, mediaQuery } from '@styles/index';
import { AnyArgVoidReturnFunction } from 'types/utils';
import { useIsMobile } from '@stores/AppCommonData';
import AquillaButton from 'aquilla/core/molecules/Button';
import VideoPlayer from '@base/VideoPlayer';

const Container = styled.div`
  padding: ${Spacings.SPACING_4B};
  display: flex;
  position: relative;
  flex-direction: column;
`;

const CloseIcon = styled(Close)`
  position: relative;
  left: ${Spacings.SPACING_5B};
  top: ${Spacings.SPACING_5B};
  margin-bottom: ${Spacings.SPACING_6B};
  cursor: pointer;
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_4B};
  }
`;

const TitleText = styled.h4`
  font-weight: ${Fonts.HEAVY};
  font-size: ${Fonts.H4};
  line-height: ${Spacings.SPACING_26};
  letter-spacing: -0.1px;
  ${mediaQuery.uptoMobile} {
    font-weight: ${Fonts.SEMIBOLD};
    font-size: ${Fonts.H5};
    line-height: ${Spacings.SPACING_6B};
    letter-spacing: 0%;
  }
`;

const BodyText = styled.p`
  font-weight: ${Fonts.LIGHT};
  font-size: ${Fonts.LARGE};
  line-height: ${Spacings.SPACING_4B};
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.P2};
    letter-spacing: 0%;
    margin-top: ${Spacings.SPACING_6};
  }
`;

const CtaWrapper = styled.div`
  margin-top: ${Spacings.SPACING_6B};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: ${Spacings.SPACING_4B};
`;

const StyAquillaButton = styled(AquillaButton)<{ $border: boolean }>`
  height: ${Spacings.SPACING_12B};
  border-radius: ${Spacings.SPACING_2B};
  font-size: ${Fonts.H6};
  font-weight: ${Fonts.HEAVY};
  width: 100%;
  border: ${({ $border }) => $border && '1px solid var(--color-base-fill)'};
`;

const VideoWrapper = styled.div`
  border-radius: 8px;
  max-width: 468px;
  margin: ${Spacings.SPACING_4B} auto 0;
  display: flex;
  justify-content: center;
`;

interface ITrialPaymentFailedDialogProps {
  showDialog: boolean;
  onClose: AnyArgVoidReturnFunction;
  redirectToHome: AnyArgVoidReturnFunction;
  url: string;
}

const TrialPaymentFailedDialog: FC<ITrialPaymentFailedDialogProps> = ({
  showDialog,
  onClose,
  redirectToHome,
  url
}) => {
  const isMobile = useIsMobile();
  return (
    <Dialog center onClose={onClose} dialogState={showDialog}>
      <CloseIcon size={isMobile ? '20px' : '24px'} onClick={onClose} />
      <Container>
        <TitleText>Oops! Your payment has failed</TitleText>
        <BodyText>
          We recommend trying again selecting a different payment method.
        </BodyText>
        {url && (
          <VideoWrapper>
            <VideoPlayer src={url} />
          </VideoWrapper>
        )}
        <CtaWrapper>
          <StyAquillaButton variant="ghost" $border onClick={redirectToHome}>
            Try later
          </StyAquillaButton>
          <StyAquillaButton variant="primary" $border={false} onClick={onClose}>
            Try again
          </StyAquillaButton>
        </CtaWrapper>
      </Container>
    </Dialog>
  );
};

export default TrialPaymentFailedDialog;
